import { useMutation } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCompanyScheduleInterviewSheet } from "./store";
import request from "@/lib/api/handler";
import { useAuth0 } from "@auth0/auth0-react";
import { QueryKeys, useInvalidateQuery } from "@/queries/utils";
import { useCompanyCandidateAvailabilityQuery } from "@/queries/company/availability";
import { useShallow } from "zustand/shallow";

export const useCompanyScheduleInterview = () => {
  const startTime = useCompanyScheduleInterviewSheet((s) => s.time);
  const date = useCompanyScheduleInterviewSheet((s) => s.selectedDate);
  const opening = useCompanyScheduleInterviewSheet((s) => s.opportunity);
  const interview = useCompanyScheduleInterviewSheet((s) => s.interview);
  const candidate = useCompanyScheduleInterviewSheet((s) => s.candidate);
  const isUpdating = useCompanyScheduleInterviewSheet((s) => s.isUpdating);
  const [opportunityID, interviewID] = useCompanyScheduleInterviewSheet(
    useShallow((s) => [s.opportunity?.id, s.interview?.id]),
  );
  const { candidateAvailability } = useCompanyCandidateAvailabilityQuery(
    opportunityID,
    interviewID,
    candidate?.candidateId,
  );

  const { getAccessTokenSilently } = useAuth0();
  const invalidate = useInvalidateQuery();

  const { isPending, error, mutate } = useMutation({
    mutationFn: async () => {
      if (!opening?.id || !interview?.id || !candidate?.id) {
        throw new Error("Missing data");
      }

      if (!startTime) {
        throw new Error("Missing start time");
      } else if (useCompanyScheduleInterviewSheet.getState().invalid) {
        throw new Error("Invalid start time");
      }

      if (!date) {
        throw new Error("Invalid date");
      }

      const candidateTimezone =
        candidateAvailability?.general?.find((a) => a.length > 0)?.[0]
          ?.timezone ??
        candidateAvailability?.specific?.find((a) => a.times.length > 0)
          ?.times[0]?.timezone;

      const timezoneParameter = { zone: candidateTimezone };

      const time = DateTime.fromISO(`${date}T${startTime}`, timezoneParameter);

      await request({
        method: isUpdating ? "PATCH" : "POST",
        path: buildPath(interview.id),
        body: JSON.stringify({
          startTime: time.toISO(),
        }),
        token: await getAccessTokenSilently(),
      });
    },
    onSettled: async () => {
      await invalidate(QueryKeys.companyOpportunities);
      await invalidate(QueryKeys.companyOpportunity(opening?.id ?? ""));

      useCompanyScheduleInterviewSheet.getState().setOpen(false);
      setTimeout(
        () => useCompanyScheduleInterviewSheet.getState().setSuccessOpen(true),
        100,
      );
    },
  });

  return {
    scheduleInterview: mutate,
    scheduleInterviewPending: isPending,
    scheduleInterviewError: error,
  };
};

const buildPath = (interviewID: string) =>
  `/api/interview/${interviewID}/schedule` as const;
